import React, { useMemo, CSSProperties, useRef } from "react";
import { darken, lighten } from "polished";
import { cx } from "@emotion/css";
import IncSmartText from "../SmartText/SmartText";
import { BarData } from "./types";

interface Props {
  data: BarData[];
  colors?: string[] | Record<string, string> | "gradient";
  onClick?: (id: string) => void;
}

export const IncHorizontalBars = (props: Props) => {
  const { data, onClick: pOnClick, colors = "gradient" } = props;

  const selectionExistsRef = useRef(false);

  const displayDataLength = useMemo(() => data.length, [data.length]);
  let maxSize = Number.NEGATIVE_INFINITY;
  const totalSize = data.reduce((acc, curr) => {
    const currSize = curr.size;
    maxSize = Math.max(maxSize, currSize);
    return acc + currSize;
  }, 0);

  const colorsArr = useMemo(() => (Array.isArray(colors) ? colors : []), [colors]);
  const colorsRec = useMemo(() => (!Array.isArray(colors) && typeof colors === "object" ? colors : {}), [colors]);

  const bars = useMemo(() => {
    let prevColor = "";
    let prevSelected = false;
    let selectionExists = false;
    const bars = data.map((datum, idx) => {
      const { label, id, size, color, selected = false, sizeLabel = `${size}` } = datum;

      const ratio = size / totalSize;
      const widthPer = ratio * 100;
      const colorRatio = (1 - size / maxSize) * 0.5;

      let finColor = color as string;
      try {
        finColor = finColor || colorsArr[idx] || colorsRec[id] || lighten(colorRatio, "#20575D");
      } catch (err) {
        console.log("Horizontal bars: Error in setting bar color", datum);
        finColor = "#20575D";
      }

      let borderColor = finColor;

      try {
        borderColor = darken(0.05, finColor);
      } catch (err) {
        console.log("Horizontal bars: Error in setting bar border color", datum);
      }

      const isLight = isColorLight(finColor);
      const isFirst = idx === 0;
      const isLast = idx === displayDataLength - 1;

      const style: CSSProperties = {
        width: `${widthPer}%`,
        minWidth: 25
      };

      const border = `1px solid ${borderColor}`;

      const barStyle: CSSProperties = {
        backgroundColor: finColor,
        borderTopLeftRadius: isFirst ? "4px" : "0px",
        borderBottomLeftRadius: isFirst ? "4px" : "0px",
        borderTopRightRadius: isLast ? "4px" : "0px",
        borderBottomRightRadius: isLast ? "4px" : "0px",
        borderLeft: prevSelected ? "none" : finColor === prevColor ? border : ""
      };

      if (isLight) {
        barStyle.borderLeft = barStyle.borderLeft ? barStyle.borderLeft : isFirst ? border : "";
        barStyle.borderRight = isLast ? border : "";
        barStyle.borderTop = border;
        barStyle.borderBottom = border;
      }

      prevColor = finColor;
      prevSelected = selected;
      selectionExists = selectionExists || selected;

      const clickDefined = pOnClick !== undefined || pOnClick !== null;
      const onClick = () => {
        pOnClick && pOnClick(id);
      };

      const barClassName = cx("inc-horizontal-bars__bar", {
        "inc-cursor-pointer": clickDefined,
        selected: selected,
        "non-selected": !selected
      });

      return (
        <div
          key={id}
          style={style}
        >
          <div className="inc-horizontal-bars__bar_label">
            <IncSmartText text={label} />
          </div>
          <div
            className={barClassName}
            onClick={onClick}
            style={barStyle}
          >
            <div className="inc-horizontal-bars__bar_value">{sizeLabel}</div>
          </div>
        </div>
      );
    });
    selectionExistsRef.current = selectionExists;
    return bars;
  }, [colorsArr, colorsRec, data, displayDataLength, maxSize, pOnClick, totalSize]);

  const className = cx("inc-horizontal-bars", {
    "inc-horizontal-bars--selection-exists": selectionExistsRef.current
  });

  return <div className={className}>{bars}</div>;
};

const isColorLight = (color: string) => {
  const replaceStr = color.length < 5 ? /./g : "";
  const colorNum = +`0x${color.slice(1).replace(replaceStr, "$&$&")}`;

  const r = colorNum >> 16;
  const g = (colorNum >> 8) & 255;
  const b = colorNum & 255;

  const hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));

  return hsp > 127.5;
};
